import React, { useState, useMemo } from 'react';
import { Table, DatePicker, Select, Badge, Button, Tag, Modal, Card, Alert, Tooltip } from 'antd';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;

const RefuelReconciliation = () => {
  const [vehicleId, setVehicleId] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedManual, setSelectedManual] = useState(null);
  const [iotModalVisible, setIotModalVisible] = useState(false);
  const [showUnlinkedOnly, setShowUnlinkedOnly] = useState(false);

  // Enhanced mock data covering all edge cases
  const manualRefuels = [
    {
      id: 1,
      sequence: 1,
      manualTime: '2025-11-22 10:30:00',
      manualDate: '2025-11-22',
      correctedTime: '2025-11-22 10:32:00',
      manualQty: 45.5,
      iotEvents: [
        { id: 1, timestamp: '2025-11-22 10:32:15', quantityLitres: 45.2, isSloshing: false, alreadyLinked: false }
      ],
      status: 'Auto-linked',
      matchQuality: 'good',
      timeDifference: 2, // minutes
      qtyDifference: 0.3,
      qtyVariancePercent: 0.66
    },
    {
      id: 2,
      sequence: 2,
      manualTime: '2025-11-22 14:15:00',
      manualDate: '2025-11-22',
      correctedTime: null,
      manualQty: 50.0,
      iotEvents: [],
      status: 'Needs review',
      matchQuality: null,
      timeDifference: null,
      qtyDifference: null,
      qtyVariancePercent: null,
      issue: 'No IoT match found'
    },
    {
      id: 3,
      sequence: 3,
      manualTime: '2025-11-22 18:45:00',
      manualDate: '2025-11-22',
      correctedTime: '2025-11-22 18:47:00',
      manualQty: 38.5,
      iotEvents: [
        { id: 2, timestamp: '2025-11-22 18:47:20', quantityLitres: 38.3, isSloshing: false, alreadyLinked: false },
        { id: 3, timestamp: '2025-11-22 18:47:45', quantityLitres: 0.2, isSloshing: false, alreadyLinked: false }
      ],
      status: 'Confirmed',
      matchQuality: 'warn',
      timeDifference: 2,
      qtyDifference: 0.0,
      qtyVariancePercent: 0.0
    },
    {
      id: 4,
      sequence: 4,
      manualTime: '2025-11-23 08:00:00', // Next day entry
      manualDate: '2025-11-23',
      correctedTime: '2025-11-22 23:45:00', // Previous night
      manualQty: 42.0,
      iotEvents: [
        { id: 4, timestamp: '2025-11-22 23:45:30', quantityLitres: 42.0, isSloshing: false, alreadyLinked: false }
      ],
      status: 'Needs review',
      matchQuality: 'warn',
      timeDifference: 495, // minutes (cross-day)
      qtyDifference: 0.0,
      qtyVariancePercent: 0.0,
      issue: 'Date mismatch - entry on next day'
    },
    {
      id: 5,
      sequence: 5,
      manualTime: '2025-11-22 16:20:00',
      manualDate: '2025-11-22',
      correctedTime: '2025-11-22 16:20:00',
      manualQty: 55.0,
      iotEvents: [
        { id: 5, timestamp: '2025-11-22 16:20:10', quantityLitres: 52.0, isSloshing: false, alreadyLinked: false },
        { id: 6, timestamp: '2025-11-22 16:20:25', quantityLitres: 1.5, isSloshing: true, alreadyLinked: false },
        { id: 7, timestamp: '2025-11-22 16:20:40', quantityLitres: 1.0, isSloshing: false, alreadyLinked: false }
      ],
      status: 'Auto-linked',
      matchQuality: 'warn',
      timeDifference: 0,
      qtyDifference: 0.5,
      qtyVariancePercent: 0.91,
      issue: 'Quantity variance > 5%'
    },
    {
      id: 6,
      sequence: 6,
      manualTime: '2025-11-22 20:00:00',
      manualDate: '2025-11-22',
      correctedTime: null,
      manualQty: 48.0,
      iotEvents: [],
      status: 'Needs review',
      matchQuality: null,
      timeDifference: null,
      qtyDifference: null,
      qtyVariancePercent: null,
      issue: 'No IoT match - possible missing detection'
    }
  ];

  // All available IoT events (including unlinked ones)
  const allIotEvents = [
    { id: 1, timestamp: '2025-11-22 10:32:15', quantityLitres: 45.2, isSloshing: false, linkedToManual: 1 },
    { id: 2, timestamp: '2025-11-22 18:47:20', quantityLitres: 38.3, isSloshing: false, linkedToManual: 3 },
    { id: 3, timestamp: '2025-11-22 18:47:45', quantityLitres: 0.2, isSloshing: false, linkedToManual: 3 },
    { id: 4, timestamp: '2025-11-22 23:45:30', quantityLitres: 42.0, isSloshing: false, linkedToManual: 4 },
    { id: 5, timestamp: '2025-11-22 16:20:10', quantityLitres: 52.0, isSloshing: false, linkedToManual: 5 },
    { id: 6, timestamp: '2025-11-22 16:20:25', quantityLitres: 1.5, isSloshing: true, linkedToManual: 5 },
    { id: 7, timestamp: '2025-11-22 16:20:40', quantityLitres: 1.0, isSloshing: false, linkedToManual: 5 },
    { id: 8, timestamp: '2025-11-22 12:30:00', quantityLitres: 35.0, isSloshing: false, linkedToManual: null }, // Unlinked
    { id: 9, timestamp: '2025-11-22 15:45:00', quantityLitres: 28.5, isSloshing: false, linkedToManual: null }, // Unlinked
  ];

  const vehicles = [
    { id: 1, name: 'Vehicle A - TCO-3904' },
    { id: 2, name: 'Vehicle B - TCO-3905' },
    { id: 3, name: 'Vehicle C - TCO-3906' }
  ];

  const openReviewModal = record => {
    setSelectedManual(record);
    setIotModalVisible(true);
  };

  const getTimeDifferenceBadge = (record) => {
    if (!record.timeDifference && record.timeDifference !== 0) return null;
    
    const diff = record.timeDifference;
    let color = 'green';
    let text = '';
    
    if (diff === 0) {
      text = 'Exact';
    } else if (diff < 5) {
      text = `${diff}m`;
      color = 'green';
    } else if (diff < 30) {
      text = `${diff}m`;
      color = 'orange';
    } else if (diff < 60) {
      text = `${diff}m`;
      color = 'red';
    } else {
      const hours = Math.floor(diff / 60);
      const mins = diff % 60;
      text = `${hours}h ${mins}m`;
      color = 'red';
    }
    
    return <Tag color={color}>{text}</Tag>;
  };

  const getQtyVarianceBadge = (record) => {
    if (record.qtyVariancePercent === null || record.qtyVariancePercent === undefined) return null;
    
    const variance = Math.abs(record.qtyVariancePercent);
    let color = 'green';
    
    if (variance > 5) color = 'red';
    else if (variance > 2) color = 'orange';
    
    return <Tag color={color}>{variance.toFixed(2)}%</Tag>;
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'sequence',
      width: 60,
      fixed: 'left'
    },
    {
      title: 'Manual Date',
      dataIndex: 'manualDate',
      width: 120,
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: 'Manual Time',
      dataIndex: 'manualTime',
      width: 140,
      render: t => t ? moment(t).format('HH:mm:ss') : '-',
    },
    {
      title: 'Corrected Time',
      dataIndex: 'correctedTime',
      width: 150,
      render: (t, record) => {
        if (!t) return <span style={{color: '#999'}}>Not set</span>;
        const formatted = moment(t).format('YYYY-MM-DD HH:mm');
        const dateDiffers = record.manualDate && !moment(t).isSame(record.manualDate, 'day');
        const timeDiffers = record.manualTime && !moment(record.manualTime).isSame(t, 'minute');
        
        return (
          <span>
            {formatted}
            {dateDiffers && <Tag color="red" style={{marginLeft: 4}}>Date Changed</Tag>}
            {timeDiffers && !dateDiffers && <Tag style={{marginLeft: 4}}>Adjusted</Tag>}
          </span>
        );
      },
    },
    {
      title: 'Time Diff',
      width: 100,
      render: (text, record) => getTimeDifferenceBadge(record)
    },
    {
      title: 'Manual Qty (L)',
      dataIndex: 'manualQty',
      width: 120,
      align: 'right'
    },
    {
      title: 'IoT Total (L)',
      width: 120,
      align: 'right',
      render: (text, record) => {
        if (!record.iotEvents || record.iotEvents.length === 0) return '-';
        const total = record.iotEvents
          .filter(e => !e.isSloshing)
          .reduce((sum, e) => sum + e.quantityLitres, 0);
        return total.toFixed(2);
      }
    },
    {
      title: 'Qty Diff',
      width: 100,
      render: (text, record) => {
        if (record.qtyDifference === null) return '-';
        const diff = record.qtyDifference;
        const color = Math.abs(diff) > 2 ? 'red' : Math.abs(diff) > 0.5 ? 'orange' : 'green';
        return <Tag color={color}>{diff > 0 ? '+' : ''}{diff.toFixed(2)}L</Tag>;
      }
    },
    {
      title: 'Qty Variance',
      width: 110,
      render: (text, record) => getQtyVarianceBadge(record)
    },
    {
      title: 'Linked IoT Events',
      width: 200,
      render: (text, record) => {
        if (!record.iotEvents || record.iotEvents.length === 0) {
          return <Tag color="red">None</Tag>;
        }
        return (
          <div>
            {record.iotEvents.map(ev => (
              <Tag 
                key={ev.id} 
                color={ev.isSloshing ? 'orange' : 'blue'}
                style={{marginBottom: 2}}
              >
                {moment(ev.timestamp).format('HH:mm')} ({ev.quantityLitres}L)
                {ev.isSloshing && ' [S]'}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Match Quality',
      dataIndex: 'matchQuality',
      width: 120,
      render: (quality, record) => {
        if (!quality) {
          return <Badge status="default" text="No match" />;
        }
        const status =
          quality === 'good'
            ? 'success'
            : quality === 'warn'
            ? 'warning'
            : 'error';
        return <Badge status={status} text={quality} />;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (status, record) => {
        let badgeStatus = 'default';
        if (status === 'Confirmed') badgeStatus = 'success';
        else if (status === 'Auto-linked') badgeStatus = 'processing';
        else if (status === 'Needs review') badgeStatus = 'error';
        return (
          <div>
            <Badge status={badgeStatus} text={status} />
            {record.issue && (
              <Tooltip title={record.issue}>
                <Tag color="red" style={{marginTop: 4, display: 'block'}}>!</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button size="small" type="primary" onClick={() => openReviewModal(record)}>
          Review
        </Button>
      ),
    },
  ];

  // Filter data based on status and unlinked
  const filteredData = useMemo(() => {
    let data = manualRefuels;
    
    if (statusFilter !== 'all') {
      data = data.filter(item => {
        if (statusFilter === 'needsReview') return item.status === 'Needs review';
        if (statusFilter === 'autoLinked') return item.status === 'Auto-linked';
        if (statusFilter === 'confirmed') return item.status === 'Confirmed';
        if (statusFilter === 'unlinked') return !item.iotEvents || item.iotEvents.length === 0;
        return true;
      });
    }
    
    if (showUnlinkedOnly) {
      data = data.filter(item => !item.iotEvents || item.iotEvents.length === 0);
    }
    
    return data;
  }, [statusFilter, showUnlinkedOnly]);

  const unlinkedIotCount = allIotEvents.filter(e => !e.linkedToManual).length;
  const unlinkedManualCount = manualRefuels.filter(m => !m.iotEvents || m.iotEvents.length === 0).length;

  return (
    <div style={{ padding: 24 }}>
      {(unlinkedManualCount > 0 || unlinkedIotCount > 0) && (
        <Alert
          message={`Unlinked Entries: ${unlinkedManualCount} manual entries and ${unlinkedIotCount} IoT events need review`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Select
          style={{ width: 220 }}
          placeholder="Select vehicle"
          value={vehicleId}
          onChange={setVehicleId}
          showSearch
          optionFilterProp="children"
        >
          {vehicles.map(v => (
            <Option key={v.id} value={v.id}>
              {v.name}
            </Option>
          ))}
        </Select>

        <RangePicker
          value={dateRange}
          onChange={setDateRange}
        />

        <Select
          style={{ width: 180 }}
          value={statusFilter}
          onChange={setStatusFilter}
        >
          <Option value="all">All Status</Option>
          <Option value="needsReview">Needs Review</Option>
          <Option value="autoLinked">Auto-linked</Option>
          <Option value="confirmed">Confirmed</Option>
          <Option value="unlinked">Unlinked Only</Option>
        </Select>

        <Button
          type={showUnlinkedOnly ? "primary" : "default"}
          onClick={() => setShowUnlinkedOnly(!showUnlinkedOnly)}
        >
          {showUnlinkedOnly ? 'Show All' : 'Show Unlinked Only'}
        </Button>
      </div>

      <Table
        rowKey="id"
        dataSource={filteredData}
        columns={columns}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 1800 }}
        size="small"
      />

      {iotModalVisible && (
        <IoTLinkModal
          visible={iotModalVisible}
          manualRefuel={selectedManual}
          allIotEvents={allIotEvents}
          onClose={() => setIotModalVisible(false)}
        />
      )}
    </div>
  );
};

// Enhanced IoT Link Modal component
const IoTLinkModal = ({ visible, manualRefuel, allIotEvents, onClose }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sloshingEvents, setSloshingEvents] = useState([]);
  const [correctedTime, setCorrectedTime] = useState(null);

  // Get available IoT events (exclude already linked ones, unless they're linked to this manual)
  const availableIotEvents = useMemo(() => {
    return allIotEvents
      .filter(e => !e.linkedToManual || e.linkedToManual === manualRefuel?.id)
      .map(e => ({
        ...e,
        isSelected: selectedRowKeys.includes(e.id),
        isSloshing: sloshingEvents.includes(e.id)
      }));
  }, [allIotEvents, manualRefuel?.id, selectedRowKeys, sloshingEvents]);

  // Calculate totals
  const selectedTotal = useMemo(() => {
    return availableIotEvents
      .filter(e => selectedRowKeys.includes(e.id) && !sloshingEvents.includes(e.id))
      .reduce((sum, e) => sum + e.quantityLitres, 0);
  }, [availableIotEvents, selectedRowKeys, sloshingEvents]);

  const manualQty = manualRefuel?.manualQty || 0;
  const qtyDifference = selectedTotal - manualQty;
  const qtyVariancePercent = manualQty > 0 ? (Math.abs(qtyDifference) / manualQty) * 100 : 0;

  // Auto-set corrected time to first selected IoT event
  React.useEffect(() => {
    if (selectedRowKeys.length > 0) {
      const firstSelected = availableIotEvents.find(e => selectedRowKeys.includes(e.id));
      if (firstSelected) {
        setCorrectedTime(firstSelected.timestamp);
      }
    } else {
      setCorrectedTime(null);
    }
  }, [selectedRowKeys, availableIotEvents]);

  const iotColumns = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      width: 180,
      render: t => moment(t).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf(),
    },
    {
      title: 'Quantity (L)',
      dataIndex: 'quantityLitres',
      width: 120,
      align: 'right',
      render: (qty, record) => (
        <span style={{ fontWeight: record.isSelected ? 'bold' : 'normal' }}>
          {qty.toFixed(2)}
        </span>
      )
    },
    {
      title: 'Status',
      width: 150,
      render: (text, record) => {
        if (sloshingEvents.includes(record.id)) {
          return <Tag color="orange">Marked as Sloshing</Tag>;
        }
        if (record.linkedToManual && record.linkedToManual === manualRefuel?.id) {
          return <Tag color="blue">Currently Linked</Tag>;
        }
        if (record.linkedToManual) {
          return <Tag color="red">Linked to Other Entry</Tag>;
        }
        return <Tag color="green">Available</Tag>;
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.linkedToManual && record.linkedToManual !== manualRefuel?.id,
    }),
  };

  const handleMarkSloshing = () => {
    setSloshingEvents([...new Set([...sloshingEvents, ...selectedRowKeys])]);
    setSelectedRowKeys([]);
  };

  const handleUnmarkSloshing = () => {
    setSloshingEvents(sloshingEvents.filter(id => !selectedRowKeys.includes(id)));
  };

  const handleSave = () => {
    // Save links, update corrected time
    const linkedEvents = availableIotEvents.filter(e => 
      selectedRowKeys.includes(e.id) && !sloshingEvents.includes(e.id)
    );
    
    console.log('Saving links:', {
      manualRefuelId: manualRefuel?.id,
      linkedIotEvents: linkedEvents.map(e => e.id),
      correctedTime: correctedTime,
      sloshingEvents: sloshingEvents,
      quantityTotal: selectedTotal,
      quantityDifference: qtyDifference
    });
    
    // Here you would call your API to save
    onClose();
  };

  return (
    <Modal
      visible={visible}
      title={`Review IoT Refuels - Manual Entry #${manualRefuel?.sequence}`}
      width={1000}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save & Link"
      okButtonProps={{ disabled: selectedRowKeys.length === 0 }}
    >
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <Card title="Manual Entry" size="small" style={{ flex: 1 }}>
          <p><strong>Date:</strong> {manualRefuel?.manualDate ? moment(manualRefuel.manualDate).format('YYYY-MM-DD') : '-'}</p>
          <p><strong>Time:</strong> {manualRefuel?.manualTime ? moment(manualRefuel.manualTime).format('HH:mm:ss') : '-'}</p>
          <p><strong>Quantity:</strong> {manualQty.toFixed(2)}L</p>
          {manualRefuel?.issue && (
            <Alert message={manualRefuel.issue} type="warning" style={{ marginTop: 8 }} />
          )}
        </Card>

        <Card title="Reconciliation Summary" size="small" style={{ flex: 1 }}>
          <p><strong>Selected IoT Total:</strong> {selectedTotal.toFixed(2)}L</p>
          <p>
            <strong>Quantity Difference:</strong> 
            <Tag color={Math.abs(qtyDifference) > 2 ? 'red' : Math.abs(qtyDifference) > 0.5 ? 'orange' : 'green'} style={{marginLeft: 8}}>
              {qtyDifference > 0 ? '+' : ''}{qtyDifference.toFixed(2)}L
            </Tag>
          </p>
          <p>
            <strong>Variance:</strong> 
            <Tag color={qtyVariancePercent > 5 ? 'red' : qtyVariancePercent > 2 ? 'orange' : 'green'} style={{marginLeft: 8}}>
              {qtyVariancePercent.toFixed(2)}%
            </Tag>
          </p>
          <p><strong>Corrected Time:</strong> {correctedTime ? moment(correctedTime).format('YYYY-MM-DD HH:mm:ss') : 'Not set'}</p>
        </Card>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        <Button 
          type="default" 
          onClick={handleMarkSloshing}
          disabled={selectedRowKeys.length === 0}
        >
          Mark Selected as Sloshing
        </Button>
        <Button 
          type="default" 
          onClick={handleUnmarkSloshing}
          disabled={selectedRowKeys.length === 0}
        >
          Unmark Selected from Sloshing
        </Button>
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm:ss"
          style={{ marginLeft: 'auto', width: 200 }}
          placeholder="Or set corrected time manually"
          value={correctedTime ? moment(correctedTime) : null}
          onChange={(value) => setCorrectedTime(value ? value.format('YYYY-MM-DD HH:mm:ss') : null)}
        />
      </div>

      <Table
        rowKey="id"
        dataSource={availableIotEvents}
        columns={iotColumns}
        rowSelection={rowSelection}
        pagination={false}
        size="small"
        scroll={{ y: 400 }}
      />
    </Modal>
  );
};

export default RefuelReconciliation;
